/**
 * Mock scoring presets for FHIR-native assessment instruments.
 *
 * Provides complete ScoringConfiguration + sample QuestionnaireItems for each
 * license-safe instrument so the UI functions identically in mock mode.
 *
 * Instruments included (all public domain / no-permission-required):
 *   PHQ-9, PHQ-8, PHQ-2, GAD-7, GAD-2, PCL-5, DES-II
 */
import type { QuestionnaireItem } from '@medplum/fhirtypes';
import type {
  ScoringConfiguration,
  ScoringOutput,
  InterpretationBand,
  InstrumentPreset,
} from '../scoringTypes';
import { ITEM_WEIGHT_URL } from '../scoringTypes';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function weightExt(value: number) {
  return [{ url: ITEM_WEIGHT_URL, valueDecimal: value }];
}

function likertOption(code: string, display: string, weight: number) {
  return {
    valueCoding: {
      code,
      display,
      extension: weightExt(weight),
    },
  };
}

const LIKERT_0_3 = [
  likertOption('0', 'Not at all', 0),
  likertOption('1', 'Several days', 1),
  likertOption('2', 'More than half the days', 2),
  likertOption('3', 'Nearly every day', 3),
];

const LIKERT_0_4 = [
  likertOption('0', 'Not at all', 0),
  likertOption('1', 'A little bit', 1),
  likertOption('2', 'Moderately', 2),
  likertOption('3', 'Quite a bit', 3),
  likertOption('4', 'Extremely', 4),
];

function scoredItem(linkId: string, text: string, options: ReturnType<typeof likertOption>[]): QuestionnaireItem {
  return {
    linkId,
    type: 'choice',
    text,
    required: true,
    answerOption: options,
  };
}

function band(id: string, label: string, low: number, high: number): InterpretationBand {
  return { id, label, low, high };
}

function output(
  id: string,
  label: string,
  codeSystem: string,
  codeValue: string,
  codeDisplay: string,
  valueType: 'integer' | 'decimal' = 'integer',
  transform: 'sum' | 'mean' = 'sum',
  includedItemLinkIds: string[] = [],
): ScoringOutput {
  return { id, label, codeSystem, codeValue, codeDisplay, valueType, includedItemLinkIds, transform };
}

// ---------------------------------------------------------------------------
// PHQ-9
// ---------------------------------------------------------------------------

const PHQ9_ITEMS: QuestionnaireItem[] = [
  scoredItem('phq9-q1', 'Little interest or pleasure in doing things', LIKERT_0_3),
  scoredItem('phq9-q2', 'Feeling down, depressed, or hopeless', LIKERT_0_3),
  scoredItem('phq9-q3', 'Trouble falling or staying asleep, or sleeping too much', LIKERT_0_3),
  scoredItem('phq9-q4', 'Feeling tired or having little energy', LIKERT_0_3),
  scoredItem('phq9-q5', 'Poor appetite or overeating', LIKERT_0_3),
  scoredItem('phq9-q6', 'Feeling bad about yourself — or that you are a failure or have let yourself or your family down', LIKERT_0_3),
  scoredItem('phq9-q7', 'Trouble concentrating on things, such as reading the newspaper or watching television', LIKERT_0_3),
  scoredItem('phq9-q8', 'Moving or speaking so slowly that other people could have noticed? Or being so fidgety or restless that you have been moving around a lot more than usual', LIKERT_0_3),
  scoredItem('phq9-q9', 'Thoughts that you would be better off dead, or of hurting yourself in some way', LIKERT_0_3),
];

const PHQ9_CONFIG: ScoringConfiguration = {
  outputs: [
    output('phq9-total', 'PHQ-9 Total Score', 'http://loinc.org', '44261-6', 'PHQ-9 total score'),
  ],
  bands: {
    'phq9-total': [
      band('phq9-b1', 'Minimal', 0, 4),
      band('phq9-b2', 'Mild', 5, 9),
      band('phq9-b3', 'Moderate', 10, 14),
      band('phq9-b4', 'Moderately Severe', 15, 19),
      band('phq9-b5', 'Severe', 20, 27),
    ],
  },
  riskFlags: [
    {
      id: 'phq9-risk-1',
      type: 'item-threshold',
      itemLinkId: 'phq9-q9',
      threshold: 1,
      comparison: '>=',
      flagCode: 'self-harm-risk',
      flagDisplay: 'Risk flag: self-harm ideation (PHQ-9 item 9)',
    },
  ],
};

// ---------------------------------------------------------------------------
// PHQ-8 (PHQ-9 without item 9)
// ---------------------------------------------------------------------------

const PHQ8_ITEMS: QuestionnaireItem[] = PHQ9_ITEMS.slice(0, 8).map((item, i) => ({
  ...item,
  linkId: `phq8-q${i + 1}`,
}));

const PHQ8_CONFIG: ScoringConfiguration = {
  outputs: [
    output('phq8-total', 'PHQ-8 Total Score', 'http://loinc.org', '44261-6', 'PHQ-8 total score'),
  ],
  bands: {
    'phq8-total': [
      band('phq8-b1', 'Minimal', 0, 4),
      band('phq8-b2', 'Mild', 5, 9),
      band('phq8-b3', 'Moderate', 10, 14),
      band('phq8-b4', 'Moderately Severe', 15, 19),
      band('phq8-b5', 'Severe', 20, 24),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// PHQ-2
// ---------------------------------------------------------------------------

const PHQ2_ITEMS: QuestionnaireItem[] = [
  scoredItem('phq2-q1', 'Little interest or pleasure in doing things', LIKERT_0_3),
  scoredItem('phq2-q2', 'Feeling down, depressed, or hopeless', LIKERT_0_3),
];

const PHQ2_CONFIG: ScoringConfiguration = {
  outputs: [
    output('phq2-total', 'PHQ-2 Total Score', 'http://loinc.org', '55758-7', 'PHQ-2 total score'),
  ],
  bands: {
    'phq2-total': [
      band('phq2-b1', 'Negative screen', 0, 2),
      band('phq2-b2', 'Positive screen', 3, 6),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// GAD-7
// ---------------------------------------------------------------------------

const GAD7_ITEMS: QuestionnaireItem[] = [
  scoredItem('gad7-q1', 'Feeling nervous, anxious, or on edge', LIKERT_0_3),
  scoredItem('gad7-q2', 'Not being able to stop or control worrying', LIKERT_0_3),
  scoredItem('gad7-q3', 'Worrying too much about different things', LIKERT_0_3),
  scoredItem('gad7-q4', 'Trouble relaxing', LIKERT_0_3),
  scoredItem('gad7-q5', 'Being so restless that it\'s hard to sit still', LIKERT_0_3),
  scoredItem('gad7-q6', 'Becoming easily annoyed or irritable', LIKERT_0_3),
  scoredItem('gad7-q7', 'Feeling afraid as if something awful might happen', LIKERT_0_3),
];

const GAD7_CONFIG: ScoringConfiguration = {
  outputs: [
    output('gad7-total', 'GAD-7 Total Score', 'http://loinc.org', '70274-6', 'GAD-7 total score'),
  ],
  bands: {
    'gad7-total': [
      band('gad7-b1', 'Minimal', 0, 4),
      band('gad7-b2', 'Mild', 5, 9),
      band('gad7-b3', 'Moderate', 10, 14),
      band('gad7-b4', 'Severe', 15, 21),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// GAD-2
// ---------------------------------------------------------------------------

const GAD2_ITEMS: QuestionnaireItem[] = [
  scoredItem('gad2-q1', 'Feeling nervous, anxious, or on edge', LIKERT_0_3),
  scoredItem('gad2-q2', 'Not being able to stop or control worrying', LIKERT_0_3),
];

const GAD2_CONFIG: ScoringConfiguration = {
  outputs: [
    output('gad2-total', 'GAD-2 Total Score', 'http://loinc.org', '63750-9', 'GAD-2 total score'),
  ],
  bands: {
    'gad2-total': [
      band('gad2-b1', 'Negative screen', 0, 2),
      band('gad2-b2', 'Positive screen', 3, 6),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// PCL-5 (20 items, 0-4 Likert)
// ---------------------------------------------------------------------------

const PCL5_ITEM_TEXTS = [
  'Repeated, disturbing, and unwanted memories of the stressful experience',
  'Repeated, disturbing dreams of the stressful experience',
  'Suddenly feeling or acting as if the stressful experience were actually happening again',
  'Feeling very upset when something reminded you of the stressful experience',
  'Having strong physical reactions when something reminded you of the stressful experience',
  'Avoiding memories, thoughts, or feelings related to the stressful experience',
  'Avoiding external reminders of the stressful experience',
  'Trouble remembering important parts of the stressful experience',
  'Having strong negative beliefs about yourself, other people, or the world',
  'Blaming yourself or someone else for the stressful experience or what happened after it',
  'Having strong negative feelings such as fear, horror, anger, guilt, or shame',
  'Loss of interest in activities that you used to enjoy',
  'Feeling distant or cut off from other people',
  'Trouble experiencing positive feelings',
  'Irritable behavior, angry outbursts, or acting aggressively',
  'Taking too many risks or doing things that could cause you harm',
  'Being "superalert" or watchful or on guard',
  'Feeling jumpy or easily startled',
  'Having difficulty concentrating',
  'Trouble falling or staying asleep',
];

const PCL5_ITEMS: QuestionnaireItem[] = PCL5_ITEM_TEXTS.map((text, i) =>
  scoredItem(`pcl5-q${i + 1}`, text, LIKERT_0_4),
);

const PCL5_CONFIG: ScoringConfiguration = {
  outputs: [
    output('pcl5-total', 'PCL-5 Total Score', 'http://loinc.org', '71956-6', 'PCL-5 total score'),
  ],
  bands: {
    'pcl5-total': [
      band('pcl5-b1', 'Below cutoff', 0, 30),
      band('pcl5-b2', 'At or above cutoff', 31, 80),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// DES-II (28 items, 0-100, scored as mean)
// ---------------------------------------------------------------------------

function desOption(value: number) {
  return likertOption(String(value), `${value}%`, value);
}

const DES_OPTIONS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(desOption);

const DES2_ITEM_TEXTS = [
  'Some people have the experience of driving or riding in a car and suddenly realizing that they don\'t remember what has happened during all or part of the trip.',
  'Some people find that sometimes they are listening to someone talk and they suddenly realize that they did not hear part or all of what was said.',
  'Some people have the experience of finding themselves in a place and having no idea how they got there.',
  'Some people have the experience of finding themselves dressed in clothes that they don\'t remember putting on.',
  'Some people have the experience of finding new things among their belongings that they do not remember buying.',
  'Some people sometimes find that they are approached by people that they do not know who call them by another name or insist that they have met them before.',
  'Some people sometimes have the experience of feeling as though they are standing next to themselves or watching themselves do something.',
  'Some people are told that they sometimes do not recognize friends or family members.',
  'Some people find that they have no memory for some important events in their lives.',
  'Some people have the experience of being accused of lying when they do not think that they have lied.',
  'Some people have the experience of looking in a mirror and not recognizing themselves.',
  'Some people have the experience of feeling that other people, objects, and the world around them are not real.',
  'Some people have the experience of feeling that their body does not seem to belong to them.',
  'Some people have the experience of sometimes remembering a past event so vividly that they feel as if they were reliving that event.',
  'Some people have the experience of not being sure whether things that they remember happening really did happen or whether they just dreamed them.',
  'Some people have the experience of being in a familiar place but finding it strange and unfamiliar.',
  'Some people find that when they are watching television or a movie they become so absorbed in the story that they are unaware of other events happening around them.',
  'Some people find that they become so involved in a fantasy or daydream that it feels as though it were really happening to them.',
  'Some people find that they sometimes are able to ignore pain.',
  'Some people find that they sometimes sit staring off into space, thinking of nothing, and are not aware of the passage of time.',
  'Some people sometimes find that when they are alone they talk out loud to themselves.',
  'Some people find that in one situation they may act so differently compared with another situation that they feel almost as if they were two different people.',
  'Some people sometimes find that in certain situations they are able to do things with amazing ease and spontaneity that would usually be difficult for them.',
  'Some people sometimes find that they cannot remember whether they have done something or have just thought about doing that thing.',
  'Some people find evidence that they have done things that they do not remember doing.',
  'Some people sometimes find writings, drawings, or notes among their belongings that they must have done but cannot remember doing.',
  'Some people sometimes find that they hear voices inside their head that tell them to do things or comment on things that they are doing.',
  'Some people sometimes feel as if they are looking at the world through a fog so that people and objects appear far away or unclear.',
];

const DES2_ITEMS: QuestionnaireItem[] = DES2_ITEM_TEXTS.map((text, i) =>
  scoredItem(`des2-q${i + 1}`, text, DES_OPTIONS),
);

const DES2_CONFIG: ScoringConfiguration = {
  outputs: [
    output('des2-mean', 'DES-II Mean Score', 'http://loinc.org', '82310-5', 'DES-II mean score', 'decimal', 'mean'),
  ],
  bands: {
    'des2-mean': [
      band('des2-b1', 'Normal range', 0, 20),
      band('des2-b2', 'Elevated', 21, 30),
      band('des2-b3', 'High (further assessment recommended)', 31, 100),
    ],
  },
  riskFlags: [],
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface InstrumentPresetData {
  label: string;
  items: QuestionnaireItem[];
  config: ScoringConfiguration;
}

const PRESETS: Record<InstrumentPreset, InstrumentPresetData> = {
  phq9: { label: 'PHQ-9', items: PHQ9_ITEMS, config: PHQ9_CONFIG },
  phq8: { label: 'PHQ-8', items: PHQ8_ITEMS, config: PHQ8_CONFIG },
  phq2: { label: 'PHQ-2', items: PHQ2_ITEMS, config: PHQ2_CONFIG },
  gad7: { label: 'GAD-7', items: GAD7_ITEMS, config: GAD7_CONFIG },
  gad2: { label: 'GAD-2', items: GAD2_ITEMS, config: GAD2_CONFIG },
  pcl5: { label: 'PCL-5', items: PCL5_ITEMS, config: PCL5_CONFIG },
  des2: { label: 'DES-II', items: DES2_ITEMS, config: DES2_CONFIG },
};

export function getPresetData(preset: InstrumentPreset): InstrumentPresetData {
  return PRESETS[preset];
}

export function getAllPresets(): Record<InstrumentPreset, InstrumentPresetData> {
  return PRESETS;
}

/** Return a default empty ScoringConfiguration. */
export function emptyScoringConfig(): ScoringConfiguration {
  return { outputs: [], bands: {}, riskFlags: [] };
}

/** Create a default "Total Score" output for a questionnaire. */
export function defaultTotalOutput(labelPrefix = 'Total'): ScoringOutput {
  return {
    id: `output-total-${Date.now()}`,
    label: `${labelPrefix} Score`,
    codeSystem: 'http://loinc.org',
    codeValue: '',
    codeDisplay: `${labelPrefix} total score`,
    valueType: 'integer',
    includedItemLinkIds: [],
    transform: 'sum',
  };
}
